import { ElementProps } from '@/components/sections/builder/types';

import {
  ColumnType,
  ElementDataType,
} from '@/components/sections/builder/elements/body/multicolumn/type';
import ImageSetting from '@/components/sections/builder/settings/image-setting';
import { Input } from '@/components/ui/input';
import { useBuilderStore } from '@/stores/builder';
import { ArrowRight, Columns } from 'lucide-react';
import React from 'react';
import { shallow } from 'zustand/shallow';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import useMediaQuery from '@/hooks/useMediaQuery';
import InputLink from '@/components/sections/builder/settings/input-link';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import Link from 'next/link';
import DynamicImage from '@/components/commons/dynamic-image';
import { cn, hexToRgba, isEmptyHTML } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { useRouter } from 'next/navigation';
import clsx from 'clsx';

const defaultData: ColumnType = {
  name: 'Column',
  heading: 'Column',
  description: 'Pair text with an image to focus on your chosen product',
  imageUrl: '',
  link: '',
  linkLabel: '',
  mobileMinWidth: 720,
};
export const ElementContent = ({ element, preview }: ElementProps) => {
  const data = (element.data || defaultData) as ColumnType;
  const parentData = element.parentData as ElementDataType;
  const themeId = element.themeId || '';
  const router = useRouter();

  const getChildValidationRules = (parentData: ElementDataType) => {
    const imagePosition = parentData.imagePosition || 'top';
    const numberOfColumns = parentData.numberOfColumns || 1;
    const isLeftPosition = imagePosition === 'left';
    const isFiveColumns = numberOfColumns === 5;

    return {
      useSmallFonts: isFiveColumns,
      enforceCompactLayout: isLeftPosition || isFiveColumns,
      forceImageRatio: isLeftPosition,
      preferredRatio: isLeftPosition ? 'square' : 'auto',
    };
  };

  const getValidatedChildFontClasses = (
    parentData: ElementDataType,
    baseClasses: string
  ): string => {
    const rules = getChildValidationRules(parentData);
    if (!rules.useSmallFonts) return baseClasses;

    return baseClasses
      .replace(/text-lg/g, 'text-base')
      .replace(/text-xl/g, 'text-lg')
      .replace(/text-2xl/g, 'text-xl')
      .replace(/md:text-lg/g, 'md:text-base')
      .replace(/lg:text-base/g, 'lg:text-sm')
      .replace(/lg:text-lg/g, 'lg:text-base');
  };

  const childRules = getChildValidationRules(parentData);
  const imgUrl =
    useMediaQuery(data.mobileMinWidth) && data.mobileImg ? data.mobileImg : data.imageUrl;

  const isLeftOrRightPosition =
    parentData.imagePosition === 'left' || parentData.imagePosition === 'right';

  return (
    <>
      <div
        className={cn(
          `flex gap-4 `,
          'color-scheme-multi-cols',
          'rounded-sm',
          'flex-col',
          parentData.secondBackground === 'true' && 'bg-secondary backdrop-blur-xl',
          parentData.imagePosition === 'left' &&
            'md:flex-col md:items-center lg:flex-row lg:items-start',
          parentData.imagePosition === 'right' &&
            'md:flex-col md:items-center lg:flex-row-reverse lg:items-center',
          parentData.imagePosition === 'top' &&
            'md:flex-col md:items-center lg:flex-col lg:items-center',
          parentData.imagePosition === 'top-left' &&
            'md:flex-col md:items-start lg:flex-col lg:items-start',
          parentData.imagePosition === 'top-right' &&
            'md:flex-col md:items-end lg:flex-col lg:items-end',
          parentData.imagePosition === 'bottom' &&
            'md:flex-col-reverse md:items-center lg:flex-col-reverse lg:items-center',
          parentData.imagePosition === 'bottom-left' &&
            'md:flex-col-reverse md:items-start lg:flex-col-reverse lg:items-start',
          parentData.imagePosition === 'bottom-right' &&
            'md:flex-col-reverse md:items-end lg:flex-col-reverse lg:items-end',
          childRules.enforceCompactLayout && 'gap-2'
        )}
        data-animation='true'
        onClick={() =>
          router.push(`${data.link}${preview ? `?theme_id=${themeId}preview=true` : ''}`)
        }
      >
        <div
          className={cn(
            'flex',
            parentData.imageWidth !== 'full_width' &&
              parentData.imagePosition?.includes('left') &&
              'justify-start md:justify-center lg:justify-start',
            parentData.imageWidth !== 'full_width' &&
              parentData.imagePosition?.includes('right') &&
              'justify-end md:justify-center lg:justify-end',
            parentData.imageWidth !== 'full_width' &&
              (parentData.imagePosition === 'top' || parentData.imagePosition === 'bottom') &&
              'justify-center'
          )}
        >
          <div
            className={cn(
              'overflow-hidden ',
              (!parentData.imageWidth ||
                ![
                  'full_width',
                  'half_width',
                  'one_third_width',
                  'small',
                  'medium',
                  'large',
                ].includes(parentData.imageWidth)) &&
                'h-32 w-32 md:h-40 md:w-40 lg:h-48 lg:w-48',
              parentData.imageWidth === 'full_width' && 'w-full',
              parentData.imageWidth === 'half_width' && 'w-1/2',
              parentData.imageWidth === 'one_third_width' && 'w-1/3',

              parentData.imageWidth === 'small' &&
                'h-full w-full md:h-[100px] md:w-[100px] xl:h-[130px] xl:w-[130px]',
              parentData.imageWidth === 'medium' &&
                'h-full w-full md:h-[140px] md:w-[140px] lg:h-[180px] lg:w-[180px]',
              parentData.imageWidth === 'large' &&
                'h-full w-full lg:h-[210px] lg:w-[210px] xl:h-[210px] xl:w-[210px]',
              childRules.enforceCompactLayout &&
                parentData.imageWidth === 'medium' &&
                'md:h-[120px] md:w-[120px] lg:h-[150px] lg:w-[150px]'
            )}
          >
            <DynamicImage
              src={imgUrl}
              alt='content'
              className={clsx(
                'mb-2 h-full w-full object-cover object-center',
                (childRules.forceImageRatio || parentData.imageRatio === 'square') &&
                  'aspect-[1/1]',
                parentData.imageRatio === 'potrait' && 'aspect-[9/16]',
                parentData.imageRatio === 'circle' && 'aspect-[1/1] rounded-full',
                childRules.forceImageRatio && !parentData.imageRatio && 'aspect-[1/1]'
              )}
              width={100}
              height={100}
              sizes='100vw'
            />
          </div>
        </div>

        {/* Content Section */}
        <div
          className={cn(
            `flex h-full w-full flex-1 flex-col justify-between space-y-2`,
            parentData.imagePosition === 'top' && 'text-center md:text-center lg:text-center',
            parentData.imagePosition === 'left' && 'text-center md:text-center lg:text-left',
            parentData.imagePosition === 'right' && 'text-center md:text-center lg:text-right',
            (parentData.imagePosition === 'top-left' ||
              parentData.imagePosition === 'bottom-left') &&
              'text-left md:text-left lg:text-left',
            (parentData.imagePosition === 'top-right' ||
              parentData.imagePosition === 'bottom-right') &&
              'text-right md:text-right lg:text-right',
            parentData.imagePosition === 'bottom' && 'text-center md:text-center lg:text-center',
            (!parentData.imagePosition ||
              ![
                'top',
                'top-left',
                'top-right',
                'bottom',
                'bottom-left',
                'bottom-right',
                'left',
                'right',
              ].includes(parentData.imagePosition)) &&
              `text-${parentData.columnAlignment || 'left'}`,
            childRules.enforceCompactLayout && 'space-y-1'
          )}
        >
          <div className={cn('space-y-2', childRules.enforceCompactLayout && 'space-y-1')}>
            <h3
              className={getValidatedChildFontClasses(
                parentData,
                cn(
                  'text_content mb-1 leading-tight',
                  parentData.mobileContentHeadingFontSize
                    ? `text-${parentData.mobileContentHeadingFontSize}`
                    : 'text-xs',
                  parentData.dekstopContentHeadingFontSize
                    ? `md:text-${parentData.dekstopContentHeadingFontSize} lg:text-${parentData.dekstopContentHeadingFontSize}`
                    : 'md:text-lg lg:text-base',
                  isLeftOrRightPosition && 'lg:line-clamp-1'
                )
              )}
              dangerouslySetInnerHTML={{ __html: data.heading || '' }}
            />
            {!isEmptyHTML(data.description) && (
              <p
                className={getValidatedChildFontClasses(
                  parentData,
                  cn(
                    'text_content inline-flex items-center space-x-2',
                    parentData.mobileContentDescriptionFontSize
                      ? `text-${parentData.mobileContentDescriptionFontSize}`
                      : 'text-xs',
                    parentData.dekstopContentDescriptionFontSize
                      ? `md:text-${parentData.dekstopContentDescriptionFontSize} lg:text-${parentData.dekstopContentDescriptionFontSize}`
                      : 'md:text-sm lg:text-base',
                    isLeftOrRightPosition && 'lg:line-clamp-3'
                  )
                )}
                dangerouslySetInnerHTML={{ __html: data.description || '' }}
                style={{
                  color: `${parentData.colorScheme?.textSecondary || '#52535B'}`,
                }}
              />
            )}
          </div>
          {/* Button/Link Section */}
          {data.linkLabel &&
            (parentData.buttonStyleColumn === 'none' || !parentData.buttonStyleColumn ? (
              <div className={cn('pt-4', childRules.enforceCompactLayout && 'pt-2')}>
                <Link href={`${data.link}${preview ? `?theme_id=${themeId}&isPreview=true` : ''}`}>
                  <h2
                    className={cn(
                      `mb-2 flex items-center gap-x-2 tracking-tight ${
                        parentData.colorScheme?.text ?? 'text-gray-900'
                      }`,
                      parentData.imagePosition === 'top' &&
                        'justify-center md:justify-center lg:justify-center',
                      parentData.imagePosition === 'left' &&
                        'justify-center md:justify-center lg:justify-start',
                      parentData.imagePosition === 'right' &&
                        'justify-center md:justify-center lg:justify-end',
                      (parentData.imagePosition === 'top-left' ||
                        parentData.imagePosition === 'bottom-left') &&
                        'justify-start md:justify-start lg:justify-start',
                      (parentData.imagePosition === 'top-right' ||
                        parentData.imagePosition === 'bottom-right') &&
                        'justify-end md:justify-end lg:justify-end',
                      parentData.imagePosition === 'bottom' &&
                        'justify-center md:justify-center lg:justify-center',
                      // Smaller font untuk compact layout
                      childRules.enforceCompactLayout && 'text-sm'
                    )}
                  >
                    {data?.linkLabel}
                    <ArrowRight
                      className={cn('h-4 w-4', childRules.enforceCompactLayout && 'h-3 w-3')}
                    />
                  </h2>
                </Link>
              </div>
            ) : (
              <div
                className={cn(
                  'mt-2',
                  parentData.imagePosition === 'top' &&
                    'flex justify-center md:justify-center lg:justify-center',
                  parentData.imagePosition === 'left' &&
                    'flex justify-center md:justify-center lg:justify-start',
                  parentData.imagePosition === 'right' &&
                    'flex justify-center md:justify-center lg:justify-end',
                  (parentData.imagePosition === 'top-left' ||
                    parentData.imagePosition === 'bottom-left') &&
                    'flex justify-start md:justify-start lg:justify-start',
                  (parentData.imagePosition === 'top-right' ||
                    parentData.imagePosition === 'bottom-right') &&
                    'flex justify-end md:justify-end lg:justify-end',
                  parentData.imagePosition === 'bottom' &&
                    'flex justify-center md:justify-center lg:justify-center'
                )}
              >
                <button
                  className={cn(
                    'button_color_scheme_multicolumn button_radius_multicolumn flex w-full items-center justify-center p-2 px-4 text-xs md:px-3 md:text-sm lg:w-auto',
                    childRules.enforceCompactLayout && 'p-1 px-3 text-xs'
                  )}
                >
                  <Link
                    href={`${data.link}${preview ? `?theme_id=${themeId}&isPreview=true` : ''}`}
                  >
                    {data?.linkLabel}
                  </Link>
                  <ArrowRight
                    className={cn('ml-2 h-4 w-4', childRules.enforceCompactLayout && 'h-3 w-3')}
                  />
                </button>
              </div>
            ))}
        </div>
        <style jsx>{`
          .bg-secondary {
            background-color: ${hexToRgba(
              parentData.secondaryBgColor?.color,
              (parentData.secondaryBgColor?.opacity ?? 0) / 100 || 0.2
            ) || 'rgba(55, 65, 81, 0.2)'};
          }
        `}</style>
      </div>
    </>
  );
};

export const ElementPreviewIcon = Columns;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ColumnType;

  return (
    <div>
      <h1 className='text-sm font-bold '>Image Settings</h1>
      <div className='mb-1 mt-2 text-xs'>Image</div>
      <ImageSetting
        value={data?.imageUrl}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            imageUrl: v,
          })
        }
      />

      <div className='mb-1 mt-4'>Mobile image (optional)</div>
      <ImageSetting
        value={data?.mobileImg}
        onChange={(val) => {
          builder.setChildData(element.parentId as string, element.id, {
            mobileImg: val,
          });
        }}
      />
      <div className='my-4 grid grid-cols-2 items-center gap-3'>
        <h4 className='text-xs'>Device under this size use mobile image</h4>
        <InputSlider
          value={data.mobileMinWidth || 0}
          max={1440}
          min={0}
          rightContent={<div>px</div>}
          onChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, {
              mobileMinWidth: val,
            })
          }
        />
      </div>
      <Separator className='my-4' />
      <h1 className='text-sm font-bold '>Heading Settings</h1>
      <div className='my-4'>
        <h3 className='mb-1 text-xs'>Heading</h3>
        <TextEditorMinimal
          value={data.heading as string}
          onChange={(val) => {
            builder.setChildData(element.parentId as string, element.id, {
              heading: val,
            });
          }}
          output='html'
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
            },
          }}
          editable
        />
      </div>
      <div className='mb-1 text-xs'>Description</div>
      <TextEditorMinimal
        value={data.description as string}
        onChange={(val) => {
          builder.setChildData(element.parentId as string, element.id, {
            description: val,
          });
        }}
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='my-4 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>Label </label>
        <Input
          onChange={(e) =>
            builder.setChildData(element.parentId as string, element.id, {
              linkLabel: e.target.value,
            })
          }
          value={data.linkLabel}
          name='linkLabel'
          type='text'
          size={100}
        />
      </div>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>Link </label>
        <InputLink
          name='url_path'
          placeholder='Search or enter a link'
          onChange={(value) => {
            builder.setChildData(element.parentId as string, element.id, {
              link: value,
            });
          }}
          value={data.link as string}
        />
      </div>
    </div>
  );
}

const Column = {
  type: 'column',
  group: 'multicolumn',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default Column;
